/**
 * White-label colour. A client picks one hex; everything else is derived, and
 * the text colour on that hex is computed rather than assumed — otherwise a
 * client who picks pale yellow gets white labels on a white-ish button.
 */
export function relativeLuminance(hex: string): number {
  const v = hex.replace("#", "");
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(v.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(hex: string, against: "#FFFFFF" | "#000000"): number {
  const a = relativeLuminance(hex);
  const b = against === "#FFFFFF" ? 1 : 0;
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Black or white, whichever is actually readable on this colour. */
export function onColour(hex: string): string {
  return contrast(hex, "#FFFFFF") >= contrast(hex, "#000000") ? "#FFFFFF" : "#101010";
}

/** True when neither black nor white reaches the 4.5:1 needed for a button. */
export function unusable(hex: string): boolean {
  return Math.max(contrast(hex, "#FFFFFF"), contrast(hex, "#000000")) < 4.5;
}

function mix(hex: string, amount: number): string {
  const v = hex.replace("#", "");
  const parts = [0, 2, 4].map((i) => {
    const c = parseInt(v.slice(i, i + 2), 16);
    return Math.round(c + (255 - c) * amount);
  });
  return "#" + parts.map((n) => n.toString(16).padStart(2, "0")).join("");
}

export function applyBrand(hex: string | null) {
  const root = document.documentElement;
  if (!hex || unusable(hex)) {
    root.style.removeProperty("--brand");
    root.style.removeProperty("--on-brand");
    root.style.removeProperty("--tint");
    return;
  }
  root.style.setProperty("--brand", hex);
  root.style.setProperty("--on-brand", onColour(hex));
  root.style.setProperty("--tint", mix(hex, 0.88));
}
